import { AxiosError, AxiosResponse } from "axios";
import { NavigateFunction } from "react-router-dom";
import { createApi } from "../../../../utils/createApi";
import { universalAPIErrorHandler } from "../../../../utils/universalAPIErrorHandler";
import {
  assertKnownSteps,
  FLOW_WINDOW_SECONDS,
  STALE_HANDSHAKE_REASONS,
  STEP_ORDER,
  TUNNEL_PLANE_STEPS,
} from "../vpnDebugConstants";
import {
  VpnDebugAnyResult,
  VpnDebugEnqueueResponse,
  VpnDebugResult,
  VpnDebugStep,
  VpnDebugStepId,
  isCustomerResult,
} from "../vpnDebugInterfaces";
import { normalizeVpnDebugResult } from "./normalizeVpnDebugResult";
import { useVpnDebuggerStore } from "../vpnDebuggerStore/vpnDebuggerStore";
import { extractVpnDebugApiError } from "./vpnDebugApiError";
import { pollVpnDebugTask } from "./pollVpnDebugTask";

/**
 * Runs the diagnostic ladder as two sequential jobs — tunnel plane, then device
 * plane — rendering each half as it lands.
 *
 * The split is not a UX nicety. `_emit_compressed` runs under `set -euo
 * pipefail` on the gateway, so a single monolithic SSM invocation loses
 * everything when any part fails, including `tunnel_down` — which is decidable
 * from the tunnel plane alone and is the commonest answer. The tunnel plane also
 * carries no credentials, so its S3-persisted SSM output holds no secret
 * material. And admin configures no CACHES, so the qcluster worker cannot
 * publish per-step progress to the web pod: two jobs are what buys progressive
 * rendering, with no new infrastructure.
 *
 * Three behaviours are load-bearing and should survive future edits:
 *   1. Polling keeps going through transient network errors until the wall clock
 *      cap (see pollVpnDebugTask). A dropped packet must not kill a two-minute job.
 *   2. Every write checks the active run token first. Without it, starting a
 *      second run lets the first one's late response overwrite the new ladder.
 *   3. Skipped steps are merged, never allowed to overwrite a completed step.
 *      The device plane replays the tunnel steps as `skipped` because it did not
 *      run them; keeping the tunnel plane's real answers is the whole point of
 *      splitting the run.
 */

const isCurrent = (token: number) =>
  token === useVpnDebuggerStore.getState().runToken;

/**
 * Merge one plane's result into the ladder.
 *
 * Exported for tests — invariant 3 is the kind of rule that quietly regresses.
 */
export const mergeVpnDebugResult = (payload: VpnDebugAnyResult | null) => {
  // Both audiences funnel through here -- tunnel plane, device plane and the
  // flow window -- so this is the one place the customer payload has to be
  // widened. Branching per renderer instead would put the tenancy boundary in
  // three components and leak through whichever one was missed.
  const result = normalizeVpnDebugResult(payload);
  if (!result) return;
  // Surface ladder drift rather than rendering an unknown step under its raw id.
  assertKnownSteps((result.steps || []).map((step) => step.id));
  const store = useVpnDebuggerStore.getState();

  const steps: Partial<Record<VpnDebugStepId, VpnDebugStep>> = {
    ...store.steps,
  };
  (result.steps || []).forEach((step) => {
    const existing = steps[step.id];
    if (
      existing &&
      existing.status !== "skipped" &&
      step.status === "skipped"
    ) {
      return;
    }
    steps[step.id] = step;
  });
  store.setSteps(steps);

  // Remember which steps a customer payload carries (a union, so a partial
  // payload can only ever add). An operator payload clears it.
  if (isCustomerResult(payload)) {
    const seen = new Set<VpnDebugStepId>(store.customerStepIds || []);
    (payload.steps || []).forEach((step) => seen.add(step.id));
    store.setCustomerStepIds(STEP_ORDER.filter((id) => seen.has(id)));
  } else {
    store.setCustomerStepIds(null);
  }

  // A plane that saw only its own half reports `inconclusive`; never let that
  // displace a real verdict the other plane already produced.
  const incoming = result.classification;
  if (incoming && (incoming.code !== "inconclusive" || !store.classification)) {
    store.setClassification(incoming);
  }
};

const postVpnDebugJob = (
  path: string,
  body: Record<string, unknown>,
  navigate: NavigateFunction,
  retryFunction: () => void,
  fallbackMessage: string
): Promise<VpnDebugEnqueueResponse> =>
  new Promise((resolve, reject) => {
    const AxiosUI = createApi("");
    AxiosUI.post(path, body).then(
      (response: AxiosResponse<VpnDebugEnqueueResponse>) => {
        resolve(response.data);
      },
      (reason: AxiosError) => {
        // When the handler decides to refresh the token and retry, it calls
        // retryFunction and this promise never settles — the retried run bumps
        // runToken, so the chain hanging off it is already dead.
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          reject(new Error(extractVpnDebugApiError(reason, fallbackMessage)));
        });
      }
    );
  });

interface RunArgs {
  navigate: NavigateFunction;
}

export const runVpnDebugLadder = async ({
  navigate,
}: RunArgs): Promise<void> => {
  const start = useVpnDebuggerStore.getState();
  const tunnelId = start.tunnelId;
  if (tunnelId == null) return;

  // Bump the token before anything else so any in-flight older run stops writing.
  const token = start.nextRunToken();
  const retryFunction = () => {
    void runVpnDebugLadder({ navigate });
  };

  const cameraId = start.selectedCameraId;
  const candidate = cameraId
    ? [
        ...(start.targets?.cameras || []),
        ...(start.targets?.other_cameras || []),
      ].find((entry) => entry.camera_id === cameraId) || null
    : null;

  const typedHost = start.host.trim();
  // The address the ladder probes. Also handed to the tunnel plane as `target`
  // so `ip route get` has something to resolve — admin's console only does that
  // on the manual-host path, which silently costs the camera path (the preferred
  // one) all three route-shaped reason codes under tunnel_status.
  const targetAddress = candidate?.ip?.trim() || typedHost;
  const hasTarget = cameraId != null || typedHost !== "";

  // Two request shapes, and which one we send is decided by whether the
  // operator changed the credential.
  //
  // Untouched: send `camera_id`. The server resolves the secret itself through
  // the CredentialOwner accessors (camera, then site), no password crosses the
  // wire, and `expected_codec` comes off the camera record so media_inspection
  // can still fail a stream that does not match what admin has recorded.
  //
  // Edited: send the manual `host` path instead. `_resolve_credential` returns
  // the *stored* secret whenever a camera_id is present and ignores anything
  // supplied alongside it, so dropping camera_id is the only way to make an
  // override take effect. The cost is `expected_codec`, which the device
  // serializer has no field for — so an overridden run cannot raise
  // `media_inspection.unexpected_encoding`. Normal runs keep it.
  const overrideCredential = cameraId != null && start.credentialEdited;
  const deviceBody: Record<string, unknown> = { tunnel_id: tunnelId };
  if (cameraId != null && !overrideCredential) {
    deviceBody.camera_id = cameraId;
  } else {
    deviceBody.host = overrideCredential ? targetAddress : typedHost;
  }
  const rtspPort = parseInt(start.rtspPort, 10);
  const onvifPort = parseInt(start.onvifPort, 10);
  if (rtspPort) deviceBody.rtsp_port = rtspPort;
  if (onvifPort) deviceBody.onvif_port = onvifPort;

  const username = start.username.trim();
  const password = start.password;
  if (username || password) {
    deviceBody.credentials = [{ username, password }];
  }

  start.resetRun();
  start.setRunning(true);
  start.setStage("Checking the tunnel on the gateway…");
  // Every step is outstanding until its plane reports.
  start.setPendingSteps([...STEP_ORDER]);
  start.setStatus("Running tunnel checks on the gateway…");

  const startedAt = Date.now();

  const finishRun = (message: string) => {
    const store = useVpnDebuggerStore.getState();
    store.setRunning(false);
    store.setStage("");
    store.setPendingSteps([]);
    store.setStatus(message);
  };

  try {
    const tunnelJob = await postVpnDebugJob(
      "network-vpn-debug/tunnel/",
      { tunnel_id: tunnelId, target: targetAddress },
      navigate,
      retryFunction,
      "Could not start the tunnel checks."
    );
    if (!isCurrent(token)) return;

    const tunnelResult = await pollVpnDebugTask(
      tunnelJob.task_id,
      token,
      startedAt,
      "Tunnel checks"
    );
    if (!isCurrent(token)) return;

    useVpnDebuggerStore
      .getState()
      .setPendingSteps(
        useVpnDebuggerStore
          .getState()
          .pendingSteps.filter((id) => !TUNNEL_PLANE_STEPS.includes(id))
      );
    mergeVpnDebugResult(tunnelResult);

    const tunnelStep = (tunnelResult?.steps || []).find(
      (step) => step.id === "tunnel_status"
    );
    // The flow window answers a TUNNEL question, so the offer has to be made
    // before the no-target exit below. Admin's console had this the other way
    // round and the button was unreachable in the one configuration an operator
    // debugging a dead tunnel actually uses: no camera, no host, just "is this
    // thing up" (actuate_admin #2899).
    const staleHandshake = Boolean(
      tunnelStep &&
        tunnelStep.status === "fail" &&
        tunnelStep.reason_code &&
        STALE_HANDSHAKE_REASONS.includes(tunnelStep.reason_code)
    );
    if (staleHandshake) {
      const current = useVpnDebuggerStore.getState();
      current.setFlowTunnelTaskId(tunnelJob.task_id);
      current.setFlowOffered(true);
    }

    if (!hasTarget) {
      // No target is a legitimate way to ask for tunnel checks alone — which is
      // why there is no separate button for it.
      finishRun(
        staleHandshake
          ? "Tunnel checks complete, but the handshake is stale."
          : "Tunnel checks complete. Pick a camera or enter a host to check a device too."
      );
      return;
    }

    if (tunnelStep && tunnelStep.status === "fail") {
      // Device checks cannot succeed through a dead tunnel, and the ladder
      // already says so. Spending another 60-120s to prove it helps nobody.
      finishRun(
        "Tunnel is down — device checks skipped. Fix the tunnel first."
      );
      return;
    }

    const running = useVpnDebuggerStore.getState();
    running.setStage("Checking the device through the tunnel…");
    running.setStatus("Running device checks (this can take a minute)…");

    // Hand the device plane this run's tunnel task id so the server can classify
    // the complete ladder. Without it each plane only ever sees its own half and
    // an all-green run reports "inconclusive".
    deviceBody.tunnel_task_id = tunnelJob.task_id;

    const deviceJob = await postVpnDebugJob(
      "network-vpn-debug/device/",
      deviceBody,
      navigate,
      retryFunction,
      "Could not start the device checks."
    );
    if (!isCurrent(token)) return;

    const deviceResult = await pollVpnDebugTask(
      deviceJob.task_id,
      token,
      startedAt,
      "Device checks"
    );
    if (!isCurrent(token)) return;

    useVpnDebuggerStore.getState().setPendingSteps([]);
    mergeVpnDebugResult(deviceResult);
    finishRun("Diagnostics complete.");
  } catch (error) {
    if (!isCurrent(token)) return;
    // Clear `running` so whatever DID complete gets a verdict rather than being
    // stuck behind a progress banner forever.
    const store = useVpnDebuggerStore.getState();
    store.setRunning(false);
    store.setStage("");
    store.setPendingSteps([]);
    store.setStatus(
      error instanceof Error ? error.message : String(error),
      true
    );
  }
};
